
import React, { useEffect, useState } from "react";
import { Card, CardBody, Col, Modal, ModalBody, ModalHeader, Row } from "reactstrap";
import TableContainer from "../../components/Common/TableContainer";
import { Column } from "react-table";
import { OrderService } from "services/OrderService";
import { AuthData, useAuth } from "context/AuthProvider";
import AdminTableContainer from "admin/dispatch/AdminTableContainer";
import { toast } from "react-toastify";
import AdminCreateBulk from "./AdminCreateBulk";
import { AdminService } from "services/AdminService";

type Delivery = {
  _id: string;
  customerName: string;
  deliveryAddress: {
    unit: string;
    street: string;
    city: string;
    contactPhone: string;
    companyName: string;
    contactName: string;
  };
  notes: string;
  totalItems: number;
  totalWeight: number;
  vehicleType: string;
  deliveryOption: string;
  subTotal: number;
};

type BulkRequest = {
  _id: string;
  bulkId: string;
  createdAt: string;
  totalDelivery: number;
  customerId: {
    firstName: string;
    lastName: string;
    phoneNumber: string;
  }
  deliveries: Delivery[];
  deliveryDateTime: string;
  pickupAddress: {
    unit: string;
    street: string;
    city: string;
    contactPhone: string;
    companyName: string;
  };
  pickupDateTime: string;
  status: string;
  updatedAt: string;
  customerName?: string;
  phoneNumber?: string;
};

const deliveryEntriesColumns: Column<Delivery>[] = [
  {
    Header: "Order Id",
    accessor: "uniqueId",
  },
  {
    Header: "Comp. Name",
    accessor: "deliveryAddress.companyName" as any,
  },
  {
    Header: "Cust. Name",
    accessor: "deliveryAddress.contactName" as any,
  },
  {
    Header: "Address",
    accessor: "deliveryAddress",
    Cell: ({ value }) => `${value.city}`
  },
  // {
  {
    Header: "Phone",
    accessor: "deliveryAddress.contactPhone" as any,
  },
  {
    Header: "Notes",
    accessor: "description" as any,
  },
  {
    Header: "Qty",
    accessor: "totalItems"
  },
  {
    Header: "Weight (bp) ",
    accessor: "totalWeight"
  },
  {
    Header: "Vehicle.T ",
    accessor: "vehicleType"
  },
  {
    Header: "RNA",
    accessor: "deliveryOption",
    Cell: ({ value }) => value
      ? value.replace(/_/g, " ").replace(/\b\w/g, char => char.toUpperCase())
      : "-"
  },
  {
    Header: "Distance",
    accessor: "totalDistance"
  },
  {
    Header: "Price",
    accessor: "subTotal",
    Cell: ({ value }: { value: number }) => (
      <span>${value.toFixed(2)}</span>
    )
  },
];
const AdminTableContent: React.FC = () => {
  const [modal, setModal] = useState(false);
  const [bulkRequests, setBulkRequests] = useState<BulkRequest[]>([]);
  const [selectedRequest, setSelectedRequest] = useState<BulkRequest | null>(null);
  const [isModified, setIsModified] = useState<boolean>(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [user] = useAuth() as AuthData;
  const [viewType, setViewType] = useState<"active" | "past">("active");

  const toggleViewModal = () => setIsViewModalOpen(!isViewModalOpen);


  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };


  const formatTime = (dateTimeString: string) => {
    const date = new Date(dateTimeString);
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  };


  const formatDateTime = (dateTimeString: string) => {
    return `${formatDate(dateTimeString)} ${formatTime(dateTimeString)}`;
  };

  const columns: Column<BulkRequest>[] = [
    {
      Header: "Date",
      accessor: "createdAt",
      Cell: ({ value }) => formatDate(value)
    },
    {
      Header: "Bulk ID",
      accessor: "bulkId",
    },
    {
      Header: "Total Delivery",
      accessor: "totalDelivery",
    },

    {
      Header: "Status",
      accessor: "status",
      Cell: ({ value }) => (
        <span
          className={`badge rounded-pill d-inline-block text-center px-3 py-1 fw-semibold text-xs
            ${value === "completed"
              ? "bg-success text-white"
              : value === "approved"
                ? "bg-success text-white"
                : value === "pending"
                  ? "bg-warning text-white"
                  : "bg-secondary text-white"
            }`}
          style={{ minWidth: "80px", fontSize: "0.75rem" }}
        >
          {value.charAt(0).toUpperCase() + value.slice(1)}
        </span>
      ),
    },
    {
      Header: "Action",
      id: "action",
      Cell: ({ row }) => (
        <div className="d-flex gap-2">
          <i
            className="bx bx-show text-secondary fs-4"
            role="button"
            title="View"
            onClick={() => handleViewClick(row.original)}
          ></i>
          <i
            className="bx bx-download text-primary fs-4"
            role="button"
            title="Download"
            onClick={() => handleDownloadPdf(row.original._id)}

          ></i>
        </div>
      ),
    },
  ];

  const handleViewClick = (request: BulkRequest) => {
    setSelectedRequest(request);
    setIsViewModalOpen(true);
  };

  useEffect(() => {
    const fetchBulkRequests = async () => {
      try {
        if (!user?._id) return;
        console.log(`🔄 Fetching ${viewType} bulk requests for user: ${user._id}`);

        if (viewType === "past") {
          const response = await AdminService.getCompletedbulkRequest();
          console.log("📦 Completed bulk request response:", response);

          if (response.success) {
            const bulkData =
              Array.isArray(response.data?.data?.data) ? response.data.data.data :
                Array.isArray(response.data?.data) ? response.data.data :
                  Array.isArray(response.data) ? response.data :
                    [];

            const totalCount =
              response.data?.data?.totalCount ??
              response.data?.totalCount ??
              bulkData.length;


            const normalizedData = bulkData.map((bulk: any) => ({
              ...bulk,
              status:
                bulk.isFullyCompleted && bulk.status?.toLowerCase() === "approved"
                  ? "completed"
                  : bulk.status,
            }));

            setBulkRequests(normalizedData);
          } else {
            console.error("❌ Error fetching past bulk requests:", response.message);
            setBulkRequests([]);
          }
        } else {
          console.log("🚀 Fetching active bulk requests...");
          const response = await AdminService.getUnCompletedbulkRequest();
          if (response.success) {
            const bulkData =
              Array.isArray(response.data?.data) ? response.data.data :
                Array.isArray(response.data) ? response.data :
                  [];
            setBulkRequests(bulkData);
          } else {
            console.error("❌ Error fetching active bulk requests:", response.message);
            setBulkRequests([]);
          }
        }
      } catch (error) {
        console.error("🔥 Failed to fetch bulk requests:", error);
        setBulkRequests([]);
      }
    };

    fetchBulkRequests();
  }, [isModified, user?._id, viewType]);


  const handleDownloadPdf = (bulkRequestId: string) => {
    if (!bulkRequestId) {
      toast.error("Invalid request ID.", {
        position: toast.POSITION.BOTTOM_RIGHT,
        className: "foo-bar",
      });
      return;
    }
    OrderService.generateBulkOrderPdf(bulkRequestId).then(res => {
      console.log("thuis sthe ", res)
      if (res) {
        try {
          const pdfBlob = new Blob([res as unknown as ArrayBuffer], { type: "application/pdf" });
          const pdfURL = URL.createObjectURL(pdfBlob);
          window.open(pdfURL, "_blank");
        } catch {
          toast.error("Something went wrong while downloading. Please try again later.", {
            position: toast.POSITION.BOTTOM_RIGHT,
            className: "foo-bar",
          });
        }
      } else {
        toast.error("Connection error. Please try again later.", {
          position: toast.POSITION.BOTTOM_RIGHT,
          className: "foo-bar",
        });
      }
    });
  };

  return (
    <Col lg={12}>
      <Card>
        <CardBody>
          {/* {bulkRequests.length === 0 ? (
            <>
              <div className="clearfix">
                <div className="d-lg-flex d-md-flex justify-content-lg-end justify-content-md-end">
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => setModal(true)}
                  >
                    New Bulk Request
                  </button>
                </div>
              </div>
              <Row className="justify-content-md-center justify-content-center align-items-center">
                <Col className="col-md-auto">
                  <div>No Bulk Requests</div>
                </Col>
              </Row>
            </>
          ) : ( */}
            <>


              <div className="mb-3 fw-semibold" style={{ fontSize: "0.95rem" }}>
                {viewType === "active" ? "Active Bulk Request Summary" : "Past Bulk Request Summary"}
              </div>

              <div className="d-flex flex-wrap justify-content-start gap-2 my-4">

                <button
                  className={`btn btn-sm ${viewType === "active" ? "btn-primary" : "btn-outline-primary"}`}
                  onClick={() => setViewType("active")}
                >
                  Active Submissions
                </button>


                <button
                  className="btn btn-outline-primary btn-sm"
                  onClick={() => setModal(true)}
                >
                  New Bulk Request
                </button>

                <button
                  className={`btn btn-sm ${viewType === "past" ? "btn-primary" : "btn-outline-primary"}`}
                  onClick={() => setViewType("past")}
                >
                  View Past Submissions
                </button>
              </div>


              <TableContainer
                key={`bulk-table-${bulkRequests.length}`}
                columns={columns}
                data={bulkRequests}
                isGlobalFilter={false}
                customPageSize={bulkRequests.length || 10} currentPage={undefined} pagelimit={undefined} handlePageChange={undefined} handleSearch={undefined} handleColumnChange={undefined} selectedColumns={undefined} showColumnChange={undefined} ispopup={undefined} isJobListGlobalFilter={undefined} isAddOptions={undefined} isAddUserList={undefined} handleOrderClicks={undefined} handleUserClick={undefined} handleCustomerClick={undefined} isAddCustList={undefined} className={undefined} isPDFGenerate={undefined} isAddressBook={undefined} hancleDeleteRecords={undefined} handleSelect={undefined} selectedID={undefined} hasCheckBox={undefined} handleSendEmail={undefined} footerData={undefined} />

            </>
          
        </CardBody>
      </Card>

      <Modal
        isOpen={isViewModalOpen}
        toggle={toggleViewModal}
        size="xl"
        style={{ maxWidth: '1500px' }}
        contentClassName="custom-modal-content"
      >
        <ModalHeader toggle={toggleViewModal}></ModalHeader>
        <ModalBody>
          <div className="px-5 py-2">
            <h4 className="bg-primary bg-opacity-25 popover-header py-3 text-center fw-bold">
              Bulk Request View
            </h4>
            {selectedRequest && (
              <div className="request-details">
                <section className="mb-4">
                  <h6 className="my-3">Customer Info</h6>
                  <div className="row ps-3">
                    <div className="col-md-3 mb-2"><div>Name:</div></div>
                    <div className="col-md-3 mb-2"><div>{selectedRequest.customerId.firstName || "N/A"}</div></div>
                    <div className="col-md-3 mb-2"><div>Phone number:</div></div>
                    <div className="col-md-3 mb-2"><div>{selectedRequest.customerId.phoneNumber || "N/A"}</div></div>
                  </div>
                </section>

                <section className="mb-4">
                  <h6 className="my-3">Pickup Details</h6>

                  <div className="row ps-3">
                    <div className="col-md-3 mb-2"><div>Pickup address:</div></div>
                    <div className="col-md-3 mb-2"><div>{`${selectedRequest.pickupAddress.unit} ${selectedRequest.pickupAddress.street} ${selectedRequest.pickupAddress.city}`}</div></div>
                    <div className="col-md-3 mb-2"><div>pick-Up date:</div></div>
                    <div className="col-md-3 mb-2"><div>{formatDate(selectedRequest.pickupDateTime)}</div></div>
                  </div>
                  <div className="row ps-3">
                    <div className="col-md-3 mb-2"><div>Pick-Up Time:</div></div>
                    <div className="col-md-3 mb-2"><div>{formatTime(selectedRequest.pickupDateTime)}</div></div>
                    <div className="col-md-3 mb-2"><div>Delivery Time:</div></div>
                    <div className="col-md-3 mb-2"><div>{formatTime(selectedRequest.deliveryDateTime)}</div></div>
                  </div>

                  <div className="row ps-3">
                    <div className="col-md-3 mb-2"><div>Vehicle Type:</div></div>
                    <div className="col-md-3 mb-2">  <div>{selectedRequest?.deliveries?.[0]?.vehicleType}</div></div>
                    <div className="col-md-3 mb-2"><div>Delivery Option:</div></div>
                    <div className="col-md-3 mb-2">
                      <div>
                        {selectedRequest?.deliveries?.[0]?.deliveryOption
                          ? selectedRequest.deliveries[0].deliveryOption
                            .replace(/_/g, " ")
                            .replace(/\b\w/g, (char) => char.toUpperCase())
                          : "-"}
                      </div>
                    </div>

                  </div>

                </section>

                <section className="mb-4">
                  <h6 className="mb-3">Delivery Entries</h6>
                  <div className="table-responsive">
                    <TableContainer
                      key={selectedRequest._id} // ✅ forces fresh table each time
                      columns={deliveryEntriesColumns}
                      data={selectedRequest.deliveries || []}
                      pagelimit={1}
                      handlePageChange={() => { }}
                      handleSearch={false}
                      handleColumnChange={() => { }}
                      selectedColumns={10}
                      showColumnChange={false}
                      customPageSize={selectedRequest.deliveries?.length || 100}
                      className="mb-3"
                      isJobListGlobalFilter={false}
                      // isChangeDateList={false}
                      // isChangeDriverList={false}
                      // isModified={false}
                      // setIsModified={() => { }}
                      isPDFGenerate={false}
                      ispopup={false}
                      isAddressBook={false} hancleDeleteRecords={undefined} handleSelect={undefined} selectedID={undefined} currentPage={undefined} isGlobalFilter={undefined} isAddOptions={undefined} isAddUserList={undefined} handleOrderClicks={undefined} handleUserClick={undefined} handleCustomerClick={undefined} isAddCustList={undefined} hasCheckBox={undefined} handleSendEmail={undefined} // getChangedStatusData={undefined}
                      footerData={undefined}                      //  currentStatus={undefined}
                    // isChangeMultipleStatus={undefined}
                    />
                  </div>

                  {selectedRequest?.deliveries && selectedRequest.deliveries.length > 0 && (
                    <div className="text-end mt-3 me-2">
                      <h6 className="fw-bold">
                        Total Price:&nbsp;
                        <span style={{ color: "#000000" }}>
                          $
                          {selectedRequest.deliveries
                            .reduce(
                              (sum: number, d: any) =>
                                sum + (Number(d.subTotal ?? 0)),
                              0
                            )
                            .toFixed(2)}
                        </span>
                      </h6>
                    </div>
                  )}

                </section>
              </div>
            )}
          </div>
        </ModalBody>
      </Modal>

      <AdminCreateBulk
        isOpen={modal}
        toggle={() => setModal(!modal)}
        isModified={isModified}
        setIsModified={setIsModified}
      />
    </Col>
  );
};

export default AdminTableContent;